import { useEffect } from 'react'
import { getToken, onMessage } from 'firebase/messaging'
import { getMessagingInstance, VAPID_KEY } from '../lib/firebase'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { useCallStore } from '../stores/callStore'
import { useUiStore } from '../stores/uiStore'
import { BASE } from '../api/client'

const STORAGE_KEY = 'fcm_token'
export const FCM_TOKEN_ID_KEY = 'fcm_token_id'

function parseJwtClaim(jwt: string, claim: string): string {
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    return payload[claim] ?? ''
  } catch {
    return ''
  }
}

async function registerToken(token: string, authToken: string): Promise<string> {
  const sessionId = parseJwtClaim(authToken, 'sid')
  const res = await fetch(`${BASE}/v1/notifications/tokens`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      platform: 'fcm',
      token,
      session_id: sessionId,
      device_name: navigator.userAgent.slice(0, 64),
    }),
  })
  if (!res.ok) throw new Error(`register token: ${res.status}`)
  const data = await res.json()
  return data.id as string
}

export function usePushNotifications() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const accessToken = useAuthStore((s) => s.accessToken)

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return

    let cancelled = false

    async function setup() {
      const addToast = useUiStore.getState().addToast
      const messaging = await getMessagingInstance()
      if (!messaging) {
        console.warn('[FCM] Firebase Messaging not supported in this browser')
        return
      }

      // НЕ запрашиваем разрешение на загрузке: это требует пользовательского
      // жеста, иначе Safari молча ставит denied (и показывает ложное «запрещено»).
      // Запрос делает кнопка «Включить уведомления» (событие 'ggram:push-enable').
      if (Notification.permission !== 'granted') {
        console.warn('[FCM] Notification permission not granted yet')
        return
      }
      if (cancelled) return

      // SW по '/' может отсутствовать (напр. firebase-messaging-sw.js
      // зарегистрирован на другом scope/CDN). Если нашли — используем, иначе
      // позволяем Firebase зарегистрировать свой SW самостоятельно.
      let swReg: ServiceWorkerRegistration | undefined
      try {
        swReg = await navigator.serviceWorker.getRegistration('/')
      } catch { /* ignore */ }

      let token: string
      try {
        token = await getToken(messaging, swReg
          ? { vapidKey: VAPID_KEY, serviceWorkerRegistration: swReg }
          : { vapidKey: VAPID_KEY })
      } catch (e) {
        console.error('[FCM] getToken failed:', e)
        addToast('error', 'Не удалось получить push-токен: ' + (e instanceof Error ? e.message : String(e)))
        return
      }

      if (!token) {
        addToast('error', 'Push-токен пустой — уведомления недоступны на этом устройстве')
        return
      }
      if (cancelled) return

      // Регистрируем токен при КАЖДОЙ загрузке (upsert на бэке идемпотентен).
      // Раньше пропускали при неизменном токене — но если сервер потерял токен
      // (напр. был удалён), клиент с тем же FCM-токеном больше не регистрировался
      // и устройство навсегда оставалось без пушей.
      try {
        const tokenId = await registerToken(token, accessToken!)
        localStorage.setItem(STORAGE_KEY, token)
        localStorage.setItem(FCM_TOKEN_ID_KEY, tokenId)
        console.info('[FCM] Push token registered', tokenId)
      } catch (e) {
        console.error('[FCM] Token registration failed:', e)
        addToast('error', 'Не удалось зарегистрировать устройство для пушей')
      }

      // Foreground: вкладка открыта — FCM не показывает уведомление сам,
      // показываем только если чат не активен
      const unsub = onMessage(messaging, async (payload) => {
        // Отмена звонка — снимаем уведомление и закрываем входящую модалку на всех вкладках.
        if (payload.data?.type === 'call_cancel') {
          const rid = payload.data?.room_id
          try {
            const reg = await navigator.serviceWorker.getRegistration()
            const ns = (await reg?.getNotifications({ tag: 'call-' + (rid ?? '') })) ?? []
            ns.forEach((n) => n.close())
          } catch { /* ignore */ }
          const inc = useCallStore.getState().incoming
          if (inc && inc.roomId === rid) useCallStore.getState().setIncoming(null)
          return
        }
        // Входящий звонок: показываем уведомление с кнопками даже в foreground
        // (пуш работает независимо от WS — модалка может не прийти, если calls-WS отвалился).
        if (payload.data?.type === 'call') {
          const d = payload.data
          try {
            const reg = await navigator.serviceWorker.getRegistration()
            await reg?.showNotification(d.title ?? 'Входящий звонок', {
              body: d.body ?? '',
              icon: '/icon.svg',
              tag: 'call-' + (d.room_id ?? ''),
              data: d,
              requireInteraction: true,
              actions: [
                { action: 'answer', title: 'Ответить' },
                { action: 'decline', title: 'Отклонить' },
              ],
            } as NotificationOptions)
          } catch { /* ignore */ }
          return
        }
        const chatId = payload.data?.chat_id
        const activeChatId = useChatStore.getState().activeChatId
        if (chatId && chatId === activeChatId) return
        if (Notification.permission !== 'granted') return

        const title = payload.data?.title ?? payload.notification?.title ?? 'GGRAM'
        const opts: NotificationOptions = {
          body: payload.data?.body ?? payload.notification?.body ?? '',
          icon: '/icon.svg',
          tag: chatId ?? 'ggram',
          data: payload.data,
          actions: [
            { action: 'open', title: 'Открыть' },
            { action: 'mark_read', title: 'Прочитано' },
          ],
        } as NotificationOptions
        // На мобильных браузерах `new Notification()` не работает —
        // показываем через ServiceWorker.showNotification (работает везде).
        try {
          const reg = await navigator.serviceWorker.getRegistration()
          if (reg) {
            await reg.showNotification(title, opts)
            return
          }
        } catch { /* fallback ниже */ }
        try {
          new Notification(title, opts)
        } catch { /* ignore — на мобиле может бросать */ }
      })

      return unsub
    }

    let cleanup: (() => void) | undefined

    function run() {
      setup().then((unsub) => {
        if (cancelled) { unsub?.(); return }
        cleanup?.()       // снимаем прошлый onMessage, если перезапускаемся
        cleanup = unsub
      })
    }
    run()

    // Кнопка «Включить уведомления» после выдачи разрешения (по жесту)
    // диспатчит это событие — регистрируем токен без перезагрузки.
    const onEnable = () => run()
    window.addEventListener('ggram:push-enable', onEnable)

    return () => {
      cancelled = true
      cleanup?.()
      window.removeEventListener('ggram:push-enable', onEnable)
    }
  }, [isAuthenticated, accessToken])
}
